/**
 * TranscriptionViewer Component
 * Displays raw transcription with option to start chat
 */

import React, { useState } from 'react';
import { Button } from '../Common/Button';

function formatDuration(seconds) {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

function estimateReadingTime(text) {
  const words = text.split(/\s+/).length;
  return Math.ceil(words / 200);
}

export function TranscriptionViewer({ 
  transcription, 
  metadata, 
  fileName,
  onStartChat,
  onReset 
}) {
  const [isExpanded, setIsExpanded] = useState(false);

  const readingTime = estimateReadingTime(transcription);
  const wordCount = transcription.split(/\s+/).length;

  return (
    <div className="animate-fade-in space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-gray-900">
            Transcripción Completa
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            {fileName} • {wordCount.toLocaleString()} palabras • ~{readingTime} min lectura
            {metadata?.duration && ` • ${formatDuration(metadata.duration)} audio`}
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Button
            onClick={() => onStartChat()}
            variant="primary"
            size="small"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 mr-2" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z" clipRule="evenodd" />
            </svg>
            Conversar con IA
          </Button>

          <Button
            onClick={onReset}
            variant="secondary"
            size="small"
          >
            Nuevo archivo
          </Button>
        </div>
      </div>

      {/* Quick actions */}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => onStartChat('executive')}
          className="px-3 py-1.5 text-sm bg-primary-50 text-primary-700 rounded-lg hover:bg-primary-100"
        >
          📋 Resumen ejecutivo
        </button>
        <button
          onClick={() => onStartChat('actions')}
          className="px-3 py-1.5 text-sm bg-green-50 text-green-700 rounded-lg hover:bg-green-100"
        >
          ✅ Tareas accionables
        </button>
        <button
          onClick={() => onStartChat('decisions')}
          className="px-3 py-1.5 text-sm bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100"
        >
          🎯 Decisiones
        </button>
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
        >
          {isExpanded ? '📄 Colapsar' : '📄 Expandir'}
        </button>
      </div>

      {/* Transcription text */}
      <div className={`bg-gray-50 rounded-xl border border-gray-200 ${isExpanded ? '' : 'max-h-[500px] overflow-hidden relative'}`}>
        <div className="p-6">
          <p className="text-gray-700 leading-relaxed whitespace-pre-wrap font-mono text-sm">
            {transcription}
          </p>
        </div>
        
        {!isExpanded && (
          <>
            <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-gray-50 to-transparent" />
            <button
              onClick={() => setIsExpanded(true)}
              className="absolute bottom-4 left-1/2 -translate-x-1/2 px-4 py-2 bg-white shadow-md rounded-full text-sm text-primary-600 hover:text-primary-700"
            >
              Ver más ↓
            </button>
          </>
        )}
      </div>
    </div>
  );
}
